import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Button from "../shared/buttons/button";
import { CalendarSvg } from "../../svgFiles/CalendarSvg";
import { ChevronDownSvg } from "../../svgFiles/ChevronDownSvg";
import {
  METRICS_PRESET_OPTIONS,
  formatISODate,
  getDraftStateFromFilter,
  getMetricsFilterLabel,
  getPresetRange,
} from "../../helpers/metricsPeriod";

function useMenuPosition(open, anchorRef) {
  const [menuStyle, setMenuStyle] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef.current) {
      setMenuStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      setMenuStyle({
        position: "fixed",
        top: rect.bottom + 8,
        left: rect.left,
        zIndex: 100001,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, anchorRef]);

  return menuStyle;
}

export default function MetricsPeriodFilter({
  value,
  onChange,
  emptyLabel = "Last month",
}) {
  const [open, setOpen] = useState(false);
  const [draftPreset, setDraftPreset] = useState(null);
  const [draftStart, setDraftStart] = useState(null);
  const [draftEnd, setDraftEnd] = useState(null);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const menuStyle = useMenuPosition(open, anchorRef);

  useEffect(() => {
    if (!open) return;
    const draft = getDraftStateFromFilter(value);
    setDraftPreset(draft.preset);
    setDraftStart(draft.start);
    setDraftEnd(draft.end);
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (
        anchorRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const handlePresetClick = (preset) => {
    setDraftPreset(preset);
    if (preset === "custom") return;
    if (preset === "all") {
      setDraftStart(null);
      setDraftEnd(null);
      return;
    }
    const [start, end] = getPresetRange(preset);
    setDraftStart(start);
    setDraftEnd(end);
  };

  const handleCalendarChange = (dates) => {
    const [start, end] = dates;
    setDraftStart(start);
    setDraftEnd(end);
    setDraftPreset("custom");
  };

  const handleClear = () => {
    setDraftPreset("all");
    setDraftStart(null);
    setDraftEnd(null);
    onChange?.({ period: "all" });
    setOpen(false);
  };

  const handleApply = () => {
    if (draftPreset === "all") {
      onChange?.({ period: "all" });
    } else if (draftPreset === "custom") {
      if (!draftStart || !draftEnd) return;
      onChange?.({
        period: "custom",
        startDate: formatISODate(draftStart),
        endDate: formatISODate(draftEnd),
      });
    } else {
      onChange?.({ period: draftPreset });
    }
    setOpen(false);
  };

  const canApply =
    Boolean(draftPreset) &&
    (draftPreset !== "custom" || (draftStart && draftEnd));

  const menu = open && menuStyle
    ? createPortal(
        <div
          ref={menuRef}
          className="metrics-period-filter__menu"
          style={menuStyle}
          role="dialog"
          aria-label="Date range filter"
        >
          <div className="metrics-period-picker">
            <ul className="metrics-period-picker__presets list-unstyled mb-0">
              {METRICS_PRESET_OPTIONS.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    className={`metrics-period-picker__preset ${
                      draftPreset === option.value ? "active" : ""
                    }`}
                    onClick={() => handlePresetClick(option.value)}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>

            <div className="metrics-period-picker__calendar">
              <DatePicker
                inline
                selectsRange
                startDate={draftStart}
                endDate={draftEnd}
                onChange={handleCalendarChange}
                maxDate={new Date()}
              />

              <div className="metrics-period-picker__actions">
                <Button
                  label="Clear"
                  className="btn-grey metrics-period-picker__clear"
                  onClick={handleClear}
                />
                <Button
                  label="Apply"
                  className="metrics-period-picker__apply"
                  onClick={handleApply}
                  disabled={!canApply}
                />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className={`metrics-period-filter ${open ? "is-open" : ""}`}>
      <button
        ref={anchorRef}
        type="button"
        className="metrics-period-filter__toggle"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="metrics-period-filter__icon">
          <CalendarSvg  size={24} width={24} height={24}/>
        </span>
        <span className="metrics-period-filter__label">
          {getMetricsFilterLabel(value, { emptyLabel })}
        </span>
      </button>
      {menu}
    </div>
  );
}
